export class ExerciseStateMachine {
  constructor(config) {
    this.currentState = config.initialState || 'READY';
    this.states = config.states || {};
    this.transitions = config.transitions || {};
    
    this.formScores = config.initialFormScores || {
      overall: 100,
      rangeOfMotion: 0,
      control: 0,
      balance: 0,
      symmetry: 0,
      velocity: 0,
      accuracy: 0
    };
    
    this.stateData = config.initialStateData || {};

    this.repetitionCount = 0;
    this.repHistory = [];  // NEW: Store per-rep data for session analytics
    this.lastTransitionTime = 0;
    this.formFeedback = null;
  }

  process(poseData, timestamp) {
    if (!this.validatePoseData(poseData)) {
      return { 
        isRepCompleted: false, 
        formFeedback: null, 
        exerciseState: this.getExerciseState() 
      };
    }

    const { landmarks, worldLandmarks } = this.extractLandmarks(poseData);
    
    const metrics = this.computeMetrics(landmarks, worldLandmarks, timestamp);
    
    this.updateStateData(metrics, timestamp);
    
    const transitionResult = this.checkTransitions(metrics, timestamp);
    
    this.updateFormScores(metrics, transitionResult.transitioned);
    
    this.formFeedback = this.generateFormFeedback(metrics, this.currentState);
    
    return {
      isRepCompleted: transitionResult.repCompleted,
      formFeedback: this.formFeedback,
      exerciseState: this.getExerciseState()
    };
  }

  checkTransitions(metrics, timestamp) {
    let transitioned = false;
    let repCompleted = false;
    
    const availableTransitions = this.transitions[this.currentState] || [];
    
    for (const transition of availableTransitions) {
      if (transition.condition(metrics, this.stateData, timestamp)) {
        if (transition.action) {
          transition.action(this, metrics, timestamp);
        }
        
        this.lastTransitionTime = timestamp;
        
        if (transition.completesRepetition) {
          this.repetitionCount++;
          repCompleted = true;

          if (transition.calculateRepScore) {
            this.stateData.repFormScore = transition.calculateRepScore(metrics, this.stateData);
          }

          // NEW: Store per-rep data for session analytics
          // Priority: use max ROM achieved during rep if tracked, otherwise use current ROM
          let repROM = 0;
          if (this.stateData.maxRangeOfMotion !== undefined) {
            // FSM tracks max ROM - use it and reset for next rep
            repROM = Math.round(this.stateData.maxRangeOfMotion);
            this.stateData.maxRangeOfMotion = 0;
          } else if (this.stateData.maxAngleReached !== undefined) {
            // FSM tracks max angle - convert to ROM percentage
            // Different FSMs have different target angles, so use metrics.rangeOfMotion as reference
            repROM = Math.round(metrics.rangeOfMotion || 0);
          } else {
            // Fallback to current ROM
            repROM = Math.round(metrics.rangeOfMotion || this.formScores.rangeOfMotion || 0);
          }
          
          this.repHistory.push({
            rep: this.repetitionCount,
            formScore: this.stateData.repFormScore || 0,
            rom: repROM, // ROM achieved in THIS rep
            timestamp: timestamp
          });
        }
        
        this.currentState = transition.targetState;
        transitioned = true;
        break;
      }
    }
    
    return { transitioned, repCompleted };
  }
  
  validatePoseData(poseData) {
    return poseData?.landmarks?.[0] && poseData?.worldLandmarks?.[0];
  }
  
  extractLandmarks(poseData) {
    return {
      landmarks: poseData.landmarks[0],
      worldLandmarks: poseData.worldLandmarks[0]
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    return {};
  }
  
  updateStateData(metrics, timestamp) {
    this.stateData = { ...this.stateData, ...metrics, lastUpdateTime: timestamp };
  }
  
  updateFormScores(metrics, transitioned) {
    // ROM: Always show CURRENT/REAL-TIME ROM for live feedback
    // Rolling averages are only for session analytics, not live display
    if (metrics.rangeOfMotion !== undefined) {
      this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion);
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    return null;
  }
  
  getExerciseState() {
    return {
      currentState: this.currentState,
      formScore: this.formScores.overall,
      repFormScore: this.stateData.repFormScore || 0,
      ...this.stateData,
      ...this.formScores
    };
  }
  
  reset() {
    if (!this.constructor.initialConfig) {
      this.constructor.initialConfig = {
        initialState: this.currentState,
        initialStateData: { ...this.stateData },
        initialFormScores: { ...this.formScores }
      };
    }
    
    this.currentState = this.constructor.initialConfig.initialState;
    this.stateData = { ...this.constructor.initialConfig.initialStateData };
    this.formScores = { ...this.constructor.initialConfig.initialFormScores };
    this.repetitionCount = 0;
    this.repHistory = [];  // NEW: Clear rep history on reset
    this.formFeedback = null;
    this.lastTransitionTime = 0;
  }
  
  getRepetitionCount() {
    return this.repetitionCount;
  }
  
  setRepetitionCount(count) {
    this.repetitionCount = count;
  }
} 